import React, {useEffect, useRef, useState} from 'react';
import DPiker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import {useTranslation} from "react-i18next";
import {useBubbleClickOutside} from "../../../services/hooks.js";
import {DATE_FORMAT_DMY2_HM} from "../../../utils/convertTime.js";
import './style.scss'
import Icon from "react-multi-date-picker/components/icon"
import classnames from "classnames";
import gregorian_en from "react-date-object/locales/gregorian_en";
import { isRTL } from '../../../services/userModel.js';

const DatePicker = ({value, onSave, format, minDate, maxDate, showTimeControl, hideDateControl, placeholder, isIcon, iconClassNames, iconRender, classNames, calendarPosition, updateValueByParent, disabled}) => {
    const ref = useRef(null);
    const [canClose, setCanClose] = useState(true);
    const {t} = useTranslation();
    const [date, setDate] = useState(() => value || new Date());

    const handleOnClose = () => {
        setCanClose(true);
        setTimeout(() => {
            if(ref.current && ref.current.closeCalendar) {
                ref.current.closeCalendar();
            }
            setDate(value);
        }, 0);
    }

    useEffect(() => {
        if(updateValueByParent) {
            setDate(value);
        }
    }, [value, updateValueByParent]);

    useBubbleClickOutside(['customDateTimePicker', 'customDateTimePickerIcon', 'rmdp-container'], handleOnClose)

    const handleOnSave = () => {
        setCanClose(true);
        onSave(date.toDate ? date.toDate() : date);
        setTimeout(() => {
            if(ref.current && ref.current.closeCalendar) {
                ref.current.closeCalendar();
            }
        }, 0);
    }

    const clampToRange = (jsDate) => {
        const min = minDate ? (minDate.toDate ? minDate.toDate() : new Date(minDate)) : null;
        const max = maxDate ? (maxDate.toDate ? maxDate.toDate() : new Date(maxDate)) : null;
        if(min && jsDate < min) return min;
        if(max && jsDate > max) return max;
        return jsDate;
    }

    const handleOnMonthChange = (data) => {
        setDate((prevDate) => {
            const prevJsDate = prevDate && prevDate.toDate ? prevDate.toDate() : new Date(prevDate);
            const updated = new Date(prevJsDate);
            updated.setMonth(data.month.index);
            return clampToRange(updated);
        });
    }

    const handleOnYearChange = (data) => {
        setDate((prevDate) => {
            const prevJsDate = prevDate && prevDate.toDate ? prevDate.toDate() : new Date(prevDate);
            const updated = new Date(prevJsDate);
            updated.setFullYear(data.year);
            return clampToRange(updated);
        });
    }

    // A FUNCTION render, not an element: given an element, react-multi-date-picker clones it and
    // injects its own props (openCalendar / handleValueChange / value), which then land on the DOM
    // node — React logged an "unrecognized prop" warning for each. As a function they arrive as
    // arguments and we consume only what we need.
    const renderIconTrigger = (value, openCalendar) => (
        <div onClick={openCalendar} className={classnames("customDateTimePickerIcon", iconClassNames)}>
            {iconRender
                ? iconRender
                : <Icon className={classnames("customDateTimePickerIcon", iconClassNames)}/>
            }
        </div>
    );

    return (
        <DPiker
            disabled={disabled}
            zIndex={1100}
            calendarPosition={calendarPosition || 'auto'}
            disableDayPicker={hideDateControl}
            portal
            onOpen={() => setCanClose(false)}
            ref={ref}
            format={format || DATE_FORMAT_DMY2_HM}
            value={date}
            placeholder={placeholder}
            locale={{
                name: 'custom_locale',
                digits: gregorian_en.digits,
                meridiems: gregorian_en.meridiems,
                weekDays: [
                    [t("datetimepicker.days.SUN"), t("datetimepicker.days.SUN")],
                    [t("datetimepicker.days.MON"), t("datetimepicker.days.MON")],
                    [t("datetimepicker.days.TUE"), t("datetimepicker.days.TUE")],
                    [t("datetimepicker.days.WED"), t("datetimepicker.days.WED")],
                    [t("datetimepicker.days.THU"), t("datetimepicker.days.THU")],
                    [t("datetimepicker.days.FRI"), t("datetimepicker.days.FRI")],
                    [t("datetimepicker.days.SAT"), t("datetimepicker.days.SAT")]
                ],
                months: [
                    [t("datetimepicker.month.JANUARY")],
                    [t("datetimepicker.month.FEBRUARY")],
                    [t("datetimepicker.month.MARCH")],
                    [t("datetimepicker.month.APRIL")],
                    [t("datetimepicker.month.MAY")],
                    [t("datetimepicker.month.JUNE")],
                    [t("datetimepicker.month.JULY")],
                    [t("datetimepicker.month.AUGUST")],
                    [t("datetimepicker.month.SEPTEMBER")],
                    [t("datetimepicker.month.OCTOBER")],
                    [t("datetimepicker.month.NOVEMBER")],
                    [t("datetimepicker.month.DECEMBER")]
                ]
            }}
            weekDays={[t("datetimepicker.days.SUN"), t("datetimepicker.days.MON"), t("datetimepicker.days.TUE"), t("datetimepicker.days.WED"), t("datetimepicker.days.THU"), t("datetimepicker.days.FRI"), t("datetimepicker.days.SAT")]}
            onChange={setDate}
            onMonthChange={handleOnMonthChange}
            onYearChange={handleOnYearChange}
            onClose={() => canClose}
            className={classnames('customDateTimePicker', classNames, {rtl: isRTL()})}
            plugins={[].concat(showTimeControl ? <TimePicker hideSeconds /> : [])}
            minDate={minDate}
            maxDate={maxDate}
            render={isIcon ? renderIconTrigger : null}
        >
            <div className={'btns'}>
                <div className={'close'} onClick={handleOnClose}>{t('basic.CLOSE')}</div>
                <div className={'save'} onClick={handleOnSave}>{t('basic.SAVE_BUTTON')}</div>
            </div>
        </DPiker>
    )
}

export default React.memo(DatePicker);